import React, { useState } from 'react';
import { 
  GraduationCap, Lock, User, Key, ArrowRight, CheckCircle2, 
  AlertCircle, Sparkles, ArrowLeft, Eye, EyeOff, ShieldCheck,
  Award, BookOpen, Layers, HelpCircle, Phone, Mail, FileCheck
} from 'lucide-react';
import { AUTH_CREDENTIALS, USER_ROLES } from '../../../data/lmsAuthData';

export const StudentAuthPage = ({ onLoginSuccess, setActiveView, setSelectedStudent }) => {
  const [activeTab, setActiveTab] = useState('login'); // 'login' | 'register'
  const [selectedProgramme, setSelectedProgramme] = useState('dba');
  const [identifier, setIdentifier] = useState('emem.udoh@student.aksu.edu.ng');
  const [password, setPassword] = useState('Scholar@AKSU2026');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [authError, setAuthError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Registration state for new scholars
  const [regAppNo, setRegAppNo] = useState('');
  const [regSurname, setRegSurname] = useState('');
  const [regNewPassword, setRegNewPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regSuccessMessage, setRegSuccessMessage] = useState(null);
  const [regError, setRegError] = useState('');

  // 6 Inaugural Cohort Candidates (DBA, MAF, DAF) + Research Fellow Tracks
  const studentPresets = [
    {
      id: 'student-dba',
      code: 'DBA',
      name: 'Emem Udoh',
      matric: 'AKSU/BS/2026/DBA/012',
      programme: 'Doctor of Business Administration',
      email: 'emem.udoh@student.aksu.edu.ng',
      password: 'Scholar@AKSU2026',
      badgeColor: 'bg-amber-100 text-[#7A470E] border-amber-300'
    },
    {
      id: 'student-dba-2',
      code: 'DBA',
      name: 'Obong Victor Okon',
      matric: 'AKSU/BS/2026/DBA/015',
      programme: 'Doctor of Business Administration',
      email: 'victor.okon@student.aksu.edu.ng',
      password: 'Scholar@AKSU2026',
      badgeColor: 'bg-amber-100 text-[#7A470E] border-amber-300'
    },
    {
      id: 'student-maf',
      code: 'MAF',
      name: 'Bassey Edet',
      matric: 'AKSU/BS/2026/MAF/018',
      programme: 'Master of Accounting and Finance',
      email: 'bassey.edet@student.aksu.edu.ng',
      password: 'Scholar@AKSU2026',
      badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-300'
    },
    {
      id: 'student-maf-2',
      code: 'MAF',
      name: 'Blessing Eno Archibong',
      matric: 'AKSU/BS/2026/MAF/022',
      programme: 'Master of Accounting and Finance',
      email: 'blessing.archibong@student.aksu.edu.ng',
      password: 'Scholar@AKSU2026',
      badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-300'
    },
    {
      id: 'student-daf',
      code: 'DAF',
      name: 'Dr. Clementina Okon',
      matric: 'AKSU/BS/2026/DAF/003',
      programme: 'Doctor of Accounting and Finance',
      email: 'clementina.okon@student.aksu.edu.ng',
      password: 'Scholar@AKSU2026',
      badgeColor: 'bg-purple-100 text-[#5C3306] border-purple-300'
    },
    {
      id: 'student-daf-2',
      code: 'DAF',
      name: 'Kufre Mfoniso James',
      matric: 'AKSU/BS/2026/DAF/007',
      programme: 'Doctor of Accounting and Finance',
      email: 'kufre.james@student.aksu.edu.ng',
      password: 'Scholar@AKSU2026',
      badgeColor: 'bg-purple-100 text-[#5C3306] border-purple-300'
    },
    {
      id: 'student-phd',
      code: 'PhD',
      name: 'Idorenyin Bassey',
      matric: 'AKSU/BS/2026/PHD/004',
      programme: 'PhD in Business Administration',
      email: 'idorenyin.bassey@student.aksu.edu.ng',
      password: 'Scholar@AKSU2026',
      badgeColor: 'bg-blue-100 text-[#0B2644] border-blue-300'
    },
    {
      id: 'student-pgd',
      code: 'PGD',
      name: 'Itoro Akpan',
      matric: 'AKSU/BS/2026/PGD/055',
      programme: 'Postgraduate Diploma (PGD)',
      email: 'itoro.akpan@student.aksu.edu.ng',
      password: 'Scholar@AKSU2026',
      badgeColor: 'bg-slate-100 text-slate-800 border-slate-300'
    }
  ];

  const handleApplyPreset = (preset) => {
    setIdentifier(preset.email);
    setPassword(preset.password);
    setSelectedProgramme(preset.code.toLowerCase());
    setAuthError('');

    // Instant sign-in convenience
    const matched = AUTH_CREDENTIALS.find(c => c.studentId === preset.id) || {
      role: USER_ROLES.STUDENT,
      studentId: preset.id,
      name: preset.name,
      matric: preset.matric,
      programme: preset.programme
    };
    if (setSelectedStudent) setSelectedStudent(preset.id);
    onLoginSuccess(matched);
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAuthError('');

    setTimeout(() => {
      setIsSubmitting(false);
      const cleanIdent = identifier.trim().toLowerCase();
      const matched = AUTH_CREDENTIALS.find(
        c => c.role === USER_ROLES.STUDENT &&
             (c.email.toLowerCase() === cleanIdent || 
              (c.demoUsername && c.demoUsername.toLowerCase() === cleanIdent) ||
              (c.matric && c.matric.toLowerCase() === cleanIdent)) &&
             c.demoPassword === password
      );

      if (matched) {
        if (setSelectedStudent && matched.studentId) {
          setSelectedStudent(matched.studentId);
        }
        onLoginSuccess(matched);
      } else {
        // Fallback demo match for any reasonable input
        if (password.length >= 4) {
          const fallback = AUTH_CREDENTIALS.find(c => c.studentId === `student-${selectedProgramme}`) || AUTH_CREDENTIALS[3];
          if (setSelectedStudent && fallback.studentId) {
            setSelectedStudent(fallback.studentId);
          }
          onLoginSuccess(fallback);
        } else {
          setAuthError('Authentication failed. Please verify your student matriculation/email or use one of the 1-Click Fast Pass credentials below.');
        }
      }
    }, 450);
  };

  const handleRegisterSubmit = (e) => {
    e.preventDefault();
    setRegError('');
    if (!regAppNo.trim() || !regSurname.trim()) {
      setRegError('Please provide your official Application Number and Surname as submitted in your application.');
      return;
    }
    if (regNewPassword.length < 6) {
      setRegError('Password must be at least 6 characters long.');
      return;
    }
    if (regNewPassword !== regConfirmPassword) {
      setRegError('Passwords do not match. Please re-enter.');
      return;
    }

    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setRegSuccessMessage({
        appNo: regAppNo.toUpperCase(),
        surname: regSurname,
        matric: `AKSU/BS/2026/EXEC/${Math.floor(100 + Math.random() * 900)}`
      });
    }, 600);
  };

  return (
    <div className="min-h-screen bg-[#030F1C] text-slate-100 flex flex-col justify-between relative overflow-hidden py-6">
      {/* Background accents */}
      <div className="absolute top-10 left-10 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#0B2644]/50 rounded-full blur-3xl pointer-events-none" />

      {/* Top Breadcrumb & Return Nav */}
      <div className="max-w-5xl mx-auto px-4 w-full mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setActiveView('lms-hub')}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#7A470E] text-white text-xs font-bold font-sc transition-colors border border-slate-700/80 cursor-pointer shadow-xs"
            >
              <ArrowLeft className="w-3.5 h-3.5 text-amber-300" />
              <span>← Back to Central Portals</span>
            </button>
            <button
              onClick={() => setActiveView('home')}
              className="text-xs text-slate-400 hover:text-white transition-colors underline hidden sm:inline ml-2"
            >
              Main Website
            </button>
          </div>

          <div className="flex items-center space-x-2 text-xs font-mono bg-emerald-950/80 border border-emerald-600/40 text-emerald-300 px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>Scholar Desk · October 2026 Session</span>
          </div>
        </div>
      </div>

      {/* Main Authentication Box */}
      <div className="max-w-5xl mx-auto px-4 w-full flex-1 flex flex-col justify-center">
        <div className="bg-gradient-to-b from-[#082038] via-[#06182B] to-[#04101D] border-2 border-slate-800 rounded-3xl p-6 sm:p-10 shadow-2xl">
          
          {/* Header Banner */}
          <div className="text-center max-w-2xl mx-auto space-y-3 mb-8">
            <div className="inline-flex items-center justify-center p-3 bg-emerald-500/10 rounded-2xl border border-emerald-400/30 text-emerald-300 shadow-md">
              <GraduationCap className="w-10 h-10" />
            </div>
            <div>
              <span className="text-xs font-mono font-bold uppercase tracking-widest text-emerald-400">
                Tier-3 Enrolled Scholars
              </span>
              <h1 className="text-2xl sm:text-4xl font-black font-sc text-white tracking-wide mt-1">
                Executive Scholar LMS Portal
              </h1>
              <p className="text-xs sm:text-sm text-slate-300 font-light mt-1">
                Access your personalized coursework, lecture recordings, syllabus case studies, and Silicon AI executive tutor.
              </p>
            </div>

            {/* Scholar LMS Authentication */}
            <div className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-[#030F1C] border border-slate-700/80 mt-2 text-xs font-sc font-bold text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>Direct Scholar LMS Access</span>
            </div>
          </div>

          {/* TAB 1: SIGN IN FORM */}
          {activeTab === 'login' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Form Side */}
              <div className="lg:col-span-7 bg-[#04101D]/80 p-6 sm:p-7 rounded-2xl border border-slate-800">
                <form onSubmit={handleLoginSubmit} className="space-y-4">
                  {authError && (
                    <div className="p-3 bg-red-950/80 border border-red-500/50 rounded-xl text-xs text-red-200 flex items-start space-x-2">
                      <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                      <span>{authError}</span>
                    </div>
                  )}

                  {/* Programme Track Selector */}
                  <div>
                    <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                      Target Academic Programme Track:
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                      {[
                        { id: 'dba', label: 'DBA', sub: 'Doctorate' },
                        { id: 'maf', label: 'MAF', sub: 'Master' },
                        { id: 'daf', label: 'DAF', sub: 'Doctorate' },
                        { id: 'phd', label: 'PhD', sub: 'Research' },
                        { id: 'pgd', label: 'PGD', sub: 'Diploma' }
                      ].map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setSelectedProgramme(p.id)}
                          className={`p-2 rounded-xl border text-center transition-all cursor-pointer ${
                            selectedProgramme === p.id 
                              ? 'bg-[#7A470E] border-amber-400 text-white font-bold shadow-xs' 
                              : 'bg-[#06182B] border-slate-800 text-slate-300 hover:border-slate-600'
                          }`}
                        >
                          <div className="text-xs font-bold font-mono">{p.label}</div>
                          <div className="text-[10px] text-slate-300 opacity-80">{p.sub}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Username / Matric Input */}
                  <div>
                    <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                      Scholar Matriculation / Portal ID / Email:
                    </label>
                    <div className="relative">
                      <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        placeholder="e.g. emem.udoh@student.aksu.edu.ng or AKSU/BS/2026/DBA/012"
                        required
                        className="w-full pl-10 pr-4 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                  </div>

                  {/* Password Input */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-bold font-sc text-slate-200">
                        Portal Security Password / PIN:
                      </label>
                      <button
                        type="button"
                        onClick={() => alert("To reset your scholar portal PIN, contact the Directorate of Academic Computing at support@businessschool.aksu.edu.ng with your verified Application Number.")}
                        className="text-[11px] text-amber-300 hover:underline cursor-pointer"
                      >
                        Forgot PIN?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••••••"
                        required
                        className="w-full pl-10 pr-10 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Session & Intake Check */}
                  <div className="flex items-center justify-between pt-1 text-xs">
                    <label className="flex items-center space-x-2 text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="rounded border-slate-700 text-[#7A470E] focus:ring-0"
                      />
                      <span className="text-[11px]">Keep Scholar Session Active</span>
                    </label>
                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-700/50">
                      Bursary Status: Cleared
                    </span>
                  </div>

                  {/* Sign In Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#0B2644] to-[#7A470E] hover:from-[#7A470E] hover:to-[#5C3306] text-white font-sc font-bold text-xs flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transition-all cursor-pointer disabled:opacity-50 mt-2"
                  >
                    <span>{isSubmitting ? 'Authenticating Scholar Record...' : 'Sign In to Student LMS Workspace'}</span>
                    <ArrowRight className="w-4 h-4 text-amber-300" />
                  </button>
                </form>
              </div>

              {/* 1-Click Fast Pass Box for Evaluators */}
              <div className="lg:col-span-5 space-y-4">
                <div className="bg-[#0B2644]/40 p-5 rounded-2xl border border-[#B87A28]/40">
                  <div className="flex items-center space-x-2 text-xs font-bold font-sc text-amber-300 mb-3">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    <span>Evaluator 1-Click Fast Pass:</span>
                  </div>
                  <p className="text-[11px] text-slate-300 mb-4 leading-relaxed">
                    Test the system instantly without memorizing credentials. Click any cohort persona below to auto-authenticate into their active Term 1 LMS:
                  </p>

                  <div className="space-y-2">
                    {studentPresets.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handleApplyPreset(preset)}
                        className="w-full text-left p-2.5 rounded-xl bg-[#06182B] hover:bg-[#7A470E] border border-slate-700/80 hover:border-amber-400 transition-all flex items-center justify-between group cursor-pointer"
                      >
                        <div className="flex items-center space-x-2.5">
                          <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${preset.badgeColor}`}>
                            {preset.code}
                          </span>
                          <div>
                            <div className="text-xs font-bold text-white group-hover:text-amber-200">
                              {preset.name}
                            </div>
                            <div className="text-[10px] text-slate-400 group-hover:text-slate-200 font-mono">
                              {preset.matric}
                            </div>
                          </div>
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-transform" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Silicon AI Notice */}
                <div className="p-4 rounded-2xl bg-emerald-950/40 border border-emerald-700/40 flex items-start space-x-3 text-xs">
                  <Sparkles className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-emerald-200 block">Integrated Silicon AI Tutor</span>
                    <span className="text-slate-300 text-[11px] leading-relaxed">
                      Your LMS workspace features an intelligent 24/7 executive study partner trained on AKSU Business School course syllabi.
                    </span>
                  </div>
                </div>

                {/* Dedicated Scholar Registration Callout for Fresh Intakes */}
                <div className="p-4 rounded-2xl bg-[#06182B] border border-[#7A470E]/50 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                  <div>
                    <span className="font-bold text-white block font-sc">Fresh Admitted Scholar?</span>
                    <span className="text-slate-400 text-[11px]">
                      Complete your matriculation clearance and course enrolment.
                    </span>
                  </div>
                  <button
                    onClick={() => setActiveView('enrolment')}
                    className="px-3 py-1.5 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold text-xs font-sc whitespace-nowrap transition-colors cursor-pointer"
                  >
                    Scholar Registration Desk →
                  </button>
                </div>
              </div>

            </div>
          )}

          {/* TAB 2: PORTAL REGISTRATION & ACTIVATION */}
          {activeTab === 'register' && (
            <div className="max-w-2xl mx-auto bg-[#04101D]/80 p-6 sm:p-8 rounded-2xl border border-slate-800">
              {regSuccessMessage ? (
                <div className="text-center py-6 space-y-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/10 border-2 border-emerald-400 text-emerald-300 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold font-sc text-white">
                      Scholar Portal Successfully Activated!
                    </h2>
                    <p className="text-xs text-slate-300 mt-1">
                      Welcome, <strong className="text-amber-300">{regSuccessMessage.surname}</strong>. Your executive credentials have been verified by the Directorate of Academic Computing.
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-[#06182B] border border-slate-700 text-left space-y-1.5 text-xs font-mono">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Application No:</span>
                      <span className="text-white font-bold">{regSuccessMessage.appNo}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Assigned Matric:</span>
                      <span className="text-emerald-300 font-bold">{regSuccessMessage.matric}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Term 1 Status:</span>
                      <span className="text-amber-300 font-bold">Active / Enrolled</span>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setActiveTab('login');
                      setIdentifier(regSuccessMessage.appNo);
                      setRegSuccessMessage(null);
                    }}
                    className="w-full py-3 px-4 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] text-white font-sc font-bold text-xs transition-colors cursor-pointer"
                  >
                    Proceed to Scholar Sign In →
                  </button>
                </div>
              ) : (
                <form onSubmit={handleRegisterSubmit} className="space-y-4">
                  <div className="border-b border-slate-800 pb-3">
                    <h2 className="text-base font-bold font-sc text-white">
                      Candidate Portal Activation & PIN Setup
                    </h2>
                    <p className="text-xs text-slate-300 mt-0.5">
                      Admitted candidates holding an official Offer Letter can activate their LMS workspace here.
                    </p>
                  </div>

                  {regError && (
                    <div className="p-3 bg-red-950/80 border border-red-500/50 rounded-xl text-xs text-red-200 flex items-start space-x-2">
                      <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                      <span>{regError}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                        Official Application No:
                      </label>
                      <input
                        type="text"
                        value={regAppNo}
                        onChange={(e) => setRegAppNo(e.target.value)}
                        placeholder="e.g. AKSU/BS/2026/APP/012"
                        required
                        className="w-full px-3.5 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 uppercase font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                        Applicant Surname:
                      </label>
                      <input
                        type="text"
                        value={regSurname}
                        onChange={(e) => setRegSurname(e.target.value)}
                        placeholder="e.g. Udoh"
                        required
                        className="w-full px-3.5 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                        Create Master Portal Password:
                      </label>
                      <input
                        type="password"
                        value={regNewPassword}
                        onChange={(e) => setRegNewPassword(e.target.value)}
                        placeholder="Min. 6 characters"
                        required
                        className="w-full px-3.5 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                        Confirm Master Password:
                      </label>
                      <input
                        type="password"
                        value={regConfirmPassword}
                        onChange={(e) => setRegConfirmPassword(e.target.value)}
                        placeholder="Re-enter password"
                        required
                        className="w-full px-3.5 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                  </div>

                  <div className="p-3 bg-amber-500/10 border border-amber-400/30 rounded-xl text-[11px] text-amber-200 flex items-start space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                    <span>
                      Activation will link your Bursary acceptance payment record and automatically enroll you into your Term 1 courseware modules.
                    </span>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-700 to-[#0B2644] hover:from-emerald-600 hover:to-[#06182B] text-white font-sc font-bold text-xs flex items-center justify-center space-x-2 shadow-lg transition-all cursor-pointer disabled:opacity-50"
                  >
                    <span>{isSubmitting ? 'Verifying Admission Offer...' : 'Activate Scholar LMS Account'}</span>
                    <ArrowRight className="w-4 h-4 text-emerald-300" />
                  </button>
                </form>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Support & Quick Contact Footer */}
      <div className="max-w-5xl mx-auto px-4 w-full mt-6 text-center text-xs text-slate-400">
        <div className="flex flex-wrap items-center justify-center gap-6 text-[11px]">
          <span className="flex items-center">
            <Phone className="w-3 h-3 text-[#B87A28] mr-1" />
            Scholar Support Desk: 0803 338 0401
          </span>
          <span className="flex items-center">
            <Mail className="w-3 h-3 text-[#B87A28] mr-1" />
            lms-support@businessschool.aksu.edu.ng
          </span>
          <button 
            onClick={() => setActiveView('apply')}
            className="text-amber-300 hover:underline cursor-pointer"
          >
            Don't have an application yet? Apply Online
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentAuthPage;
